/**
 * `src/lib/audio/queue.ts` — what plays next. PURE: no DOM, no network.
 *
 * Playback starts at the ayah the user tapped and continues ayah by ayah to
 * the END OF THAT SURAH, then stops. It never runs on into the next surah: the
 * user asked to listen to a place, not to the rest of the mushaf.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE BASMALA POLICY
 * ═══════════════════════════════════════════════════════════════════════════
 * everyayah.com publishes one file per ayah, `SSSAAA.mp3`, and its ayah-1
 * files carry NO basmala (measured: 112:1 is ~3 s at 64 kbps, under half of
 * the basmala alone). So:
 *
 *   · al-Fatiha (1): the basmala IS ayah 1 — 001001 — and nothing is added.
 *   · at-Tawbah (9): has no basmala. Nothing is added, ever.
 *   · every other surah, when playback STARTS AT AYAH 1: the reciter's own
 *     001001 (al-Fatiha's first ayah, which is the basmala) is played first,
 *     as the mushaf writes it above the surah.
 *   · playback started mid-surah: no basmala. It is recited at the opening of
 *     a surah, and starting at 2:255 is not opening al-Baqarah.
 *
 * "Previous" from ayah 1 goes back to that basmala lead-in where there is
 * one; "previous" from the lead-in restarts it.
 */

import { ayahCountOf, isSurah } from '../anchor';

export const EVERYAYAH_DATA = 'https://everyayah.com/data/';

export interface Track {
	surah: number;
	/** the ayah; on a basmala lead-in, the ayah it leads into (always 1) */
	ayah: number;
	/** `true` for the basmala played before ayah 1 */
	basmala: boolean;
}

const pad3 = (n: number) => String(n).padStart(3, '0');

/** `002255.mp3` */
export function ayahFileName(surah: number, ayah: number): string {
	return `${pad3(surah)}${pad3(ayah)}.mp3`;
}

/** The everyayah.com URL of a track for one reciter folder. */
export function trackUrl(reciterId: string, track: Track): string {
	const file = track.basmala ? ayahFileName(1, 1) : ayahFileName(track.surah, track.ayah);
	return `${EVERYAYAH_DATA}${encodeURIComponent(reciterId)}/${file}`;
}

/** Does this surah get the basmala lead-in when played from its start? */
export function hasBasmalaLeadIn(surah: number): boolean {
	return surah !== 1 && surah !== 9;
}

function assertPlace(surah: number, ayah: number): void {
	if (!isSurah(surah) || !Number.isInteger(ayah) || ayah < 1 || ayah > ayahCountOf(surah)) {
		throw new RangeError(`no ayah ${surah}:${ayah}`);
	}
}

/** Where playback starts when the user taps listen on `surah:ayah`. */
export function firstTrack(surah: number, ayah: number): Track {
	assertPlace(surah, ayah);
	return { surah, ayah, basmala: ayah === 1 && hasBasmalaLeadIn(surah) };
}

/** The track after `track`, or `null` at the end of the surah. */
export function nextTrack(track: Track): Track | null {
	if (track.basmala) return { surah: track.surah, ayah: 1, basmala: false };
	if (track.ayah < ayahCountOf(track.surah)) {
		return { surah: track.surah, ayah: track.ayah + 1, basmala: false };
	}
	return null;
}

/** The track before `track`. Never leaves the surah; at its start it stays. */
export function previousTrack(track: Track): Track {
	if (track.basmala) return track;
	if (track.ayah > 1) return { surah: track.surah, ayah: track.ayah - 1, basmala: false };
	return hasBasmalaLeadIn(track.surah) ? { surah: track.surah, ayah: 1, basmala: true } : track;
}

export function sameTrack(a: Track | null, b: Track | null): boolean {
	return !!a && !!b && a.surah === b.surah && a.ayah === b.ayah && a.basmala === b.basmala;
}
